import React from "react";

// The end of the conversations list, said out loud.
//
// WHY IT EXISTS. The list column and the detail column are one grid row, so they
// are the same height, and the transcript is usually the taller of the two. On a
// property with few conversations the list therefore stops partway down its own
// column — 397px short of the bottom on one measured client — and the last row's
// divider then reads as the place the list was cut off rather than the place it
// ended. Nothing is wrong there: the list is complete. It just does not look it.
//
// AND IT ANSWERS A REAL QUESTION. The list pages in the background, so "is that
// everything, or is it still loading?" is a question a reader can genuinely have
// — which is why this renders only once the background fill has finished, and
// why it states a count rather than a bare "end of list".
//
// IT IS NOT AN EMPTY STATE. Nothing is missing and nothing has gone wrong, so it
// borrows none of that treatment: no icon, no heading, no call to action, no
// centred block of explanation. A quiet rule with a caption in it, in the muted
// grey the panel captions already use.
//
// The caption is a rule-caption-rule, not a bordered strip. A strip with a top
// border under the last row is indistinguishable from one more row divider,
// which would make the list look MORE cut off rather than less.
//
// Grey on grey, never a hue: these panels are a customer-reporting surface and
// get printed and photocopied, so nothing here may depend on colour to be read.
//
// It is rendered in the list's own flow, so on a list long enough to scroll it
// simply waits at the bottom, where "you have reached the end" is exactly what a
// reader wants told. No measurement, no branch on how much space is left.
const ListEndMarker = ({ shown, total, complete }) => {
  // Nothing to close off, or the set is still filling. Either way, saying "end"
  // would be a claim about data that is not all here yet.
  if (!complete || !shown) return null;

  // THREE STATEMENTS, and which one is true depends on what the page knows.
  //
  // `shown` is what the reader actually scrolled past — the filtered count when
  // a filter is on. `total` is the property's count for this modality, which
  // arrives from its own query and is 0 until it does.
  //
  //   total unknown        → "N conversations". The count is certain; whether it
  //                          is ALL of them is not, so it is not claimed. This
  //                          is a real state, not a defensive branch: the count
  //                          query can be slower than the rows, and under the
  //                          e2e list stub it never answers at all.
  //   shown < total        → "N of M match". Saying "all 29" while a filter
  //                          hides 36 more is a false statement about the
  //                          property, not a shorthand — the reader concludes
  //                          those 36 do not exist.
  //   shown === total      → "all N conversations". Everything there is.
  const known = total > 0;
  const filtered = known && shown < total;
  const one = shown === 1;
  const label = filtered
    ? `End of list · ${shown} of ${total} ${one ? "matches" : "match"}`
    : one
      ? "End of list · 1 conversation"
      : known
        ? `End of list · all ${shown} conversations`
        : `End of list · ${shown} conversations`;

  return (
    <div className="flex items-center gap-3 px-4 py-5 select-none" data-testid="list-end-marker">
      <span className="h-px flex-1 bg-gray-300 dark:bg-gray-700" aria-hidden="true" />
      <span className="gi-label text-gray-500 dark:text-gray-400 whitespace-nowrap">{label}</span>
      <span className="h-px flex-1 bg-gray-300 dark:bg-gray-700" aria-hidden="true" />
    </div>
  );
};

export default ListEndMarker;
